import { useCallback, useEffect, useState } from "react";
import { usePreferences } from "./usePreferences";
import type { ThemeChoice } from "./usePreferences";

/**
 * Room lights. The stylesheet does the work — every token swaps on [data-theme="light"] — so this
 * hook only owns the attribute and the resolution of "system" into an actual theme (§20, §21).
 *
 * "System" is live, not a snapshot taken at sign-in: an OS that switches to dark at sunset has to
 * reach an open tab without a reload.
 */
export type ResolvedTheme = "dark" | "light";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Cycled by the masthead button, in the order that makes "system" easy to get back to. */
const NEXT_CHOICE: Record<ThemeChoice, ThemeChoice> = {
  system: "light",
  light: "dark",
  dark: "system",
};

/** Names the action, not the state: the button says what pressing it will do. */
export const THEME_ACTION: Record<ThemeChoice, string> = {
  system: "Use the light theme",
  light: "Use the dark theme",
  dark: "Follow the system theme",
};

export interface ThemeControl {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
  cycle: () => void;
}

function readSystemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

export function useTheme(): ThemeControl {
  const [preferences, setPreferences] = usePreferences();
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(readSystemTheme);

  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? "dark" : "light");
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const resolved: ResolvedTheme = preferences.theme === "system" ? systemTheme : preferences.theme;

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolved);
  }, [resolved]);

  const setChoice = useCallback(
    (choice: ThemeChoice) => setPreferences({ theme: choice }),
    [setPreferences],
  );

  const cycle = useCallback(
    () => setPreferences((previous) => ({ theme: NEXT_CHOICE[previous.theme] })),
    [setPreferences],
  );

  return { choice: preferences.theme, resolved, setChoice, cycle };
}
